import { ListProps, Paging } from '@Core';
import { PTZPatrolMode } from '@EcamModel/model/PTZCamera';
import {
    BatteryStatus,
    Camera,
    CameraPhotos,
    CameraWithInfo,
    CaptureInfoUploadMode,
    ExternalBatteryPackMode,
    PurposeType,
    Status,
    WifiStatus,
} from '../model';

export interface IGetCameraProps extends ListProps<Camera> {
    Status?: Status;
    LocationId?: number;
    ZoneIds?: number[];
    Battery?: BatteryStatus;
    Wifi?: WifiStatus;
    From?: Date;
    To?: Date;
    Mac?: string;
    NotUpdatedToday?: CameraUpdatedToday;
    SortByField?: SortByField;
    SortByValue?: SortByValue;
    UploadMode?: CaptureInfoUploadMode;
    Purposes?: PurposeType;
    ExternalBatteryPack?: ExternalBatteryPackMode;
}

export interface IGetPTZCameraProps extends ListProps<Camera> {
    Status?: Status;
    From?: Date;
    To?: Date;
    Mac?: string;
    SortByField?: SortByField;
    SortByValue?: SortByValue;
    ZoneIds?: number[];
    LocationId?: number;
    HasExtendedCoverage?: boolean;
}

export interface IGetSP111CameraProps extends ListProps<Camera> {
    Status?: Status;
    From?: Date;
    To?: Date;
    Mac?: string;
    SortByField?: SortByField;
    SortByValue?: SortByValue;
    ZoneIds?: number[];
    LocationId?: number;
}

export interface IGetAllCameraProps extends IGetCameraProps {
    CameraType?: CameraType;
    HasExtendedCoverage?: boolean;
}

export interface IGetPMC8266CameraProps extends ListProps<Camera> {
    Status?: Status;
    From?: Date;
    To?: Date;
    Mac?: string;
    SortByField?: SortByField;
    SortByValue?: SortByValue;
    ZoneIds?: number[];
    LocationId?: number;
}

export interface IGetCameraTaskProps extends ListProps<Camera> {
    LocationId?: number;
    ZoneIds?: number[];
    SortByField?: SortByField;
    SortByValue?: SortByValue;
}

export enum SortByValue {
    Desc,
    Asc,
}

export enum SortByField {
    Zone,
    BatteryLevel,
    Updated,
    CameraName,
}

export enum CameraUpdatedToday {
    None,
    Updated,
}

export enum CameraType {
    Solar,
    PTZ,
    SP111,
    PMC8266,
}

export interface IUpsertCameraProps extends Camera {
    Mac: string;
    PatrolMode?: PTZPatrolMode;
    ExtendedCoverage?: ExtendedCoverage[];
    EvPermitZoneIds?: number[];
}

export interface ExtendedCoverage {
    LocationId: number;
    ZoneIds: number[];
}

export interface IUpdateConfigIsVerificationsEnabled {
    CameraId: number;
    Enable: boolean;
}

export interface IUpdateConfigIsVerificationsEnabledResponse {
    IsVerificationsEnabled: boolean;
    EnablePcnVerificationAt: Date;
}

export interface IUpdateConfigEnableGroupMatching {
    CameraId: number;
    Enable: boolean;
}

export interface IUpdateConfigEnableGroupMatchingResponse {
    EnableGroupMatchingAt?: Date;
}

export interface ISetCameraPhoto {
    CameraId: number;
    CameraPhotoId: number;
    PhotoType: CameraPhotoType;
}

export interface IChangeCameraStatusProps {
    CameraId: number;
    IsActiveAllGeoZones?: boolean;
}

export enum CameraPhotoType {
    Original = 'Original',
    LatestEdit = 'LatestEdit',
}

export interface ISwapMacCamera {
    currentCameraId: number;
    targetCameraId: number;
}

export interface CameraStatisticsByServiceType {
    Solar4gCameraCount: number;
    PTZCameraCount: number;
    SP111CameraCount: number;
    PMC8266CameraCount: number;
}

export interface IGetListCamera extends ListProps<Camera> {
    From?: Date;
    To?: Date;
}

export interface IGetCamerasWithoutSIM extends ListProps<Camera> {
    IsNoSIM?: boolean;
}

export interface ICameraHttpController {
    changeStatus(props: IChangeCameraStatusProps): Promise<Camera>;
    upsertWithSolarCamera(props: IUpsertCameraProps): Promise<CameraWithInfo>;
    upsertWithPTZCamera(props: IUpsertCameraProps): Promise<CameraWithInfo>;
    upsertWithSP111Camera(props: IUpsertCameraProps): Promise<CameraWithInfo>;
    list(props: IGetCameraProps): Promise<Paging<CameraWithInfo>>;
    listPTZCamera(props: IGetPTZCameraProps): Promise<Paging<CameraWithInfo>>;
    listSP111Camera(props: IGetSP111CameraProps): Promise<Paging<CameraWithInfo>>;
    listCameraAbleToCreateTask(props: IGetCameraTaskProps): Promise<Paging<CameraWithInfo>>;
    wakeCameraByManually(id: number);
    getLatestCameraPhoto(cameraId: number): Promise<CameraPhotos>;
    getListMacCameraInActive(): Promise<string[]>;
    checkANPRCameraExisted(anprCameraId: number): Promise<boolean>;
    updateConfigIsVerificationsEnabled(
        props: IUpdateConfigIsVerificationsEnabled
    ): Promise<IUpdateConfigIsVerificationsEnabledResponse>;

    updateConfigEnableGroupMatching(
        props: IUpdateConfigEnableGroupMatching
    ): Promise<IUpdateConfigEnableGroupMatchingResponse>;

    setCameraPhoto(props: ISetCameraPhoto): Promise<CameraPhotos>;
    swapMacCamera(props: ISwapMacCamera): Promise<string>;
    getCameraStatisticsByServiceType(): Promise<CameraStatisticsByServiceType>;
    upsertWithPMC8266Camera(props: IUpsertCameraProps): Promise<CameraWithInfo>;
    listPMC8266Camera(props: IGetPMC8266CameraProps): Promise<Paging<CameraWithInfo>>;
    getLatestCameraPhotoPMC8266(cameraId: number): Promise<CameraPhotos>;
    getListCamera(props: IGetListCamera): Promise<Paging<Camera>>;
    getCamerasWithoutSIM(props: IGetCamerasWithoutSIM): Promise<Paging<Camera>>;
    getLensCapacity(): Promise<string[]>;
}
